import {FeatureToggle} from '@/config/featureToggles'
import {z} from 'zod'

// Can be used to enable features in development
export const isDev = () => {
  return import.meta.env.MODE === 'development'
}

// Can be used to enable different behaviors on code during development for debuging purpose
// Activated via query-string (?debug=true)
export const isDebug = () => {
  const hasDebugParam = new URLSearchParams(window.location.search).get('debug') === 'true'
  return isDev() && hasDebugParam
}

// Returns true when running the System Under Test
// Can be used to disable features and change behaviors
export const isSUT = () => {
  return ['test', 'sut', 'e2e'].includes(import.meta.env.MODE)
}

// MARK: - Env variables validator

const EnvRequired = z.string()

const EnvFeatureToggle = (name: string) =>
  z
    .string()
    .optional()
    .transform(value => {
      if (!isSUT() && isDev() && [undefined, null, ''].includes(value)) {
        console.warn(`[FeatureToggle] ${name} not set, will assume "Disabled"`)
      }

      return !!value
    })

z.object({
  // Required env variables for the app to work
  VITE_API_URL: EnvRequired,
  // VITE_API_EVENTS_URL: EnvRequired,
  VITE_API_ADMIN_URL: EnvRequired,
  // VITE_COUNTRIES: EnvRequired,
  // VITE_APPLICATIONS: EnvRequired,
  VITE_API_ADMIN_URL_ENDPOINT_SUFFIX: EnvFeatureToggle('VITE_API_ADMIN_URL_ENDPOINT_SUFFIX'),

  // Feature toggles (optional env variables, will assume `false` when not set)
  [FeatureToggle.customSettings]: EnvFeatureToggle(FeatureToggle.customSettings),
  [FeatureToggle.dataReports]: EnvFeatureToggle(FeatureToggle.dataReports),
  [FeatureToggle.journeyType]: EnvFeatureToggle(FeatureToggle.journeyType),
  [FeatureToggle.targetAudience]: EnvFeatureToggle(FeatureToggle.targetAudience),
  [FeatureToggle.recurrence]: EnvFeatureToggle(FeatureToggle.recurrence),
  [FeatureToggle.recurrenceDaySelector]: EnvFeatureToggle(FeatureToggle.recurrenceDaySelector),
  [FeatureToggle.scheduleDateRange]: EnvFeatureToggle(FeatureToggle.scheduleDateRange),
  [FeatureToggle.sendPushCustomImage]: EnvFeatureToggle(FeatureToggle.sendPushCustomImage),
  [FeatureToggle.sendPushCustomLandingPage]: EnvFeatureToggle(FeatureToggle.sendPushCustomLandingPage),
  [FeatureToggle.timeExecutionWindow]: EnvFeatureToggle(FeatureToggle.timeExecutionWindow),
  [FeatureToggle.userRole]: EnvFeatureToggle(FeatureToggle.userRole),
  [FeatureToggle.traitCategory]: EnvFeatureToggle(FeatureToggle.traitCategory),
  [FeatureToggle.paramsDeliveryPoints]: EnvFeatureToggle(FeatureToggle.paramsDeliveryPoints),
  [FeatureToggle.traitsOrders]: EnvFeatureToggle(FeatureToggle.traitsOrders),
  [FeatureToggle.traitsPoints]: EnvFeatureToggle(FeatureToggle.traitsPoints),
  [FeatureToggle.traitsPromotions]: EnvFeatureToggle(FeatureToggle.traitsPromotions),
  [FeatureToggle.realTimeNotificationPage]: EnvFeatureToggle(FeatureToggle.realTimeNotificationPage),
  [FeatureToggle.realTimeNotificationDuration]: EnvFeatureToggle(FeatureToggle.realTimeNotificationDuration),
  [FeatureToggle.realTimeNotificationTimeExecutionWindow]: EnvFeatureToggle(
    FeatureToggle.realTimeNotificationTimeExecutionWindow
  ),
  [FeatureToggle.realTimeNotificationEditDetails]: EnvFeatureToggle(FeatureToggle.realTimeNotificationEditDetails),
  [FeatureToggle.messageTemplates]: EnvFeatureToggle(FeatureToggle.messageTemplates),
  [FeatureToggle.whatsAppTemplates]: EnvFeatureToggle(FeatureToggle.whatsAppTemplates),
  [FeatureToggle.emailTemplates]: EnvFeatureToggle(FeatureToggle.emailTemplates),
  [FeatureToggle.guidexEditor]: EnvFeatureToggle(FeatureToggle.guidexEditor),
  [FeatureToggle.marketsPage]: EnvFeatureToggle(FeatureToggle.marketsPage),

  // Steps
  [FeatureToggle.stepRequiresIntervention]: EnvFeatureToggle(FeatureToggle.stepRequiresIntervention),
  [FeatureToggle.stepSendPush]: EnvFeatureToggle(FeatureToggle.stepSendPush),
  [FeatureToggle.stepSendSms]: EnvFeatureToggle(FeatureToggle.stepSendSms),
  [FeatureToggle.stepSendWhatsApp]: EnvFeatureToggle(FeatureToggle.stepSendWhatsApp),
  [FeatureToggle.stepSendEmail]: EnvFeatureToggle(FeatureToggle.stepSendEmail),

  // WIP
  [FeatureToggle.editPublished]: EnvFeatureToggle(FeatureToggle.editPublished),
  [FeatureToggle.sendPushLanguageButtons]: EnvFeatureToggle(FeatureToggle.sendPushLanguageButtons),
}).parse(import.meta.env)
